import React, { useState } from 'react';
import { LayoutGridIcon, ListIcon } from 'lucide-react';
import { Container, Section } from '../components/ui/Section';
import { PageHeader } from '../components/ui/PageHeader';
import { EventCard } from '../components/events/EventCard';
import { EventList } from '../components/events/EventList';
import { EmptyState } from '../components/ui/EmptyState';
import { ButtonExternal } from '../components/ui/Button';
import { TypicalWeek } from '../components/schedule/TypicalWeek';
import { WelcomeWeek } from '../components/welcome/WelcomeWeek';
import { ScheduleChannels } from '../components/schedule/ScheduleChannels';
import { Reveal, stagger } from '../components/motion/Reveal';
import { DoodleUnderline } from '../components/brand/Doodles';
import { DoodleScatter } from '../components/brand/DoodleScatter';
import { visibleSpecialEvents } from '../data/events';
import { externalLinks, officialChannels } from '../data/site';
type View = 'cards' | 'list';
const views: {
  id: View;
  label: string;
  icon: typeof ListIcon;
}[] = [{
  id: 'cards',
  label: 'Card view',
  icon: LayoutGridIcon
}, {
  id: 'list',
  label: 'List view',
  icon: ListIcon
}];
export function Events() {
  const [view, setView] = useState<View>('cards');
  const hasEvents = visibleSpecialEvents.length > 0;
  return <>
      <PageHeader eyebrow="Events" title="Events at UCF BCM" intro="Welcome Week, worship nights, community events, and opportunities to connect — here is what is happening at BCM." seed="events-header" data-id="element-471" data-mp="1h3suyqmjw7ou" />

      {/* SEASONAL — the primary date-specific group while Welcome Week is on. */}
      <WelcomeWeek headingId="welcome-week-events-heading" variant="events" data-id="element-472" data-mp="1352vj4c9dvxb" />

      {/* PERMANENT CONTENT — useful even when no special events are active. */}
      <TypicalWeek showPhoto={false} data-id="element-473" data-mp="angtogofm9p1" />
      <ScheduleChannels headingId="events-channels-heading" data-id="element-474" data-mp="amfhu9zldj69" />

      {/* TIME-SENSITIVE CONTENT — verified, date-specific events only. */}
      <Section className="relative overflow-hidden bg-[#fffdf6] py-14 sm:py-18" labelledBy="special-events-heading" data-id="element-475" data-mp="vd17tbfl10cs">
        <DoodleScatter tone="white" seed="special-events" data-id="element-476" data-mp="zdl4yf05o3yx" />
        <Container className="relative" data-id="element-477" data-mp="145cwosv1sfqn">
          <Reveal data-id="element-478" data-mp="146e9p2vmjcb0">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" data-id="element-479" data-mp="145wfscwfpa69">
              <div className="max-w-2xl" data-id="element-480" data-mp="193xyuxtv19hq">
                <h2 id="special-events-heading" className="font-display text-3xl font-extrabold leading-tight tracking-tight text-brand-navy sm:text-[2.5rem]" data-id="element-481" data-mp="ce6v07zoi9z9">
                  Upcoming special events
                </h2>
                <p className="mt-3 font-sans text-[16px] leading-relaxed text-ink" data-id="element-482" data-mp="lgy8seg11hu2">
                  Date-specific events appear here once BCM leadership confirms the details.
                </p>
              </div>

              {hasEvents ? <div role="group" aria-label="Choose event layout" className="flex w-fit shrink-0 rounded-full border-2 border-brand-navy bg-white p-1" data-id="element-483" data-mp="21sbytj4iefvx">
                  {views.map(option => {
                const isActive = view === option.id;
                return <button key={option.id} type="button" aria-pressed={isActive} onClick={() => setView(option.id)} className={`inline-flex min-h-[44px] items-center gap-2 rounded-full px-4 font-meta text-[13px] font-semibold transition-colors ${isActive ? 'bg-brand-navy text-white' : 'text-brand-navy hover:bg-brand-tan'}`} data-id="element-484" data-mp="3cizwmmjycqh5">
                        <option.icon aria-hidden="true" className="h-4 w-4" data-id="element-485" data-mp="ruc5bdhgbjn2" />
                        {option.label}
                      </button>;
              })}
                </div> : null}
            </div>
          </Reveal>

          <div className="mt-8" data-id="element-486" data-mp="zurxjtx5zuam">
            {hasEvents ? view === 'cards' ? <ul className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-id="element-487" data-mp="kxirdu37clyu">
                  {visibleSpecialEvents.map((event, index) => <Reveal key={event.id} as="li" delay={stagger(index)} className="h-full" data-id="element-488" data-mp="pzq7mvv2eo6z" data-mpi={index}>
                      <EventCard event={event} data-id="element-489" data-mp="c45pi2n5ua0m" />
                    </Reveal>)}
                </ul> : <Reveal data-id="element-490" data-mp="28azgmglxcnt">
                  <EventList events={visibleSpecialEvents} data-id="element-491" data-mp="293zl4l293wz" />
                </Reveal> : <Reveal data-id="element-492" data-mp="77nn39faynrg">
                <EmptyState label="Coming soon" title="New events are being finalized." copy="Check Instagram, KnightConnect, or Discord for the latest schedule." tone="tan" data-id="element-493" data-mp="751ed6goe27n">
                  {officialChannels.map(channel => <ButtonExternal key={channel.id} href={channel.href} variant="secondary" data-id="element-494" data-mp="35r8k9eal7ls6">
                      {channel.title}
                    </ButtonExternal>)}
                </EmptyState>
              </Reveal>}
          </div>

        </Container>
      </Section>

      <Section className="relative overflow-hidden bg-brand-blue py-14 sm:py-20" labelledBy="events-cta-heading" data-id="element-495" data-mp="3too3iywc5s2l">
        <DoodleScatter tone="blue" seed="events-cta" layout="narrow" data-id="element-496" data-mp="3op1pwocli9t9" />
        <Container className="relative max-w-3xl text-center" data-id="element-497" data-mp="6cd9bm6cepvt">
          <Reveal data-id="element-498" data-mp="6dodvo7m41nz">
            <h2 id="events-cta-heading" className="font-display text-3xl font-extrabold leading-tight tracking-tight text-brand-navy sm:text-5xl" data-id="element-499" data-mp="2aib4qjpksm9">
              Want someone to keep you in the loop?
            </h2>
            <DoodleUnderline className="mx-auto mt-3 h-3.5 w-48" color="var(--bcm-dark-blue)" data-id="element-500" data-mp="bcrk3bzbwdxj" />
            <div className="mt-7 flex justify-center" data-id="element-501" data-mp="354hi428k0f7b">
              <ButtonExternal href={externalLinks.getConnectedForm} data-id="element-502" data-mp="394030y0dkqa5">Get Connected</ButtonExternal>
            </div>
          </Reveal>
        </Container>
      </Section>
    </>;
}